import React, { useCallback, useRef } from 'react';
import { RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { MEMORIAL_PHOTO_LINK_SAFE_AREA } from '../../content/memorialKeepsakesContent';
import { getProductFrameClass } from '../../utils/memorialPhotoLinkBuilder';
import { KeepsakeFrameMockup } from './KeepsakeFrameMockup';
import { KeepsakeImageEnhanceControls } from './KeepsakeImageEnhanceControls';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function KeepsakeImagePositionEditor({
  imageUrl,
  imagePosition,
  imageEdits,
  orientation,
  product,
  frameStyle,
  layout,
  name,
  dates,
  memorialTitle,
  published,
  onChange,
  onReset,
  onFit,
  onFill,
  onEditsChange,
  onResetEdits,
  onRotateLeft,
  onRotateRight,
}) {
  const dragRef = useRef(false);

  const updatePosition = useCallback(
    (patch) => onChange({ ...imagePosition, ...patch }),
    [imagePosition, onChange],
  );

  const handlePointer = useCallback(
    (clientX, clientY, target) => {
      const rect = target.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      updatePosition({
        x: clamp(((clientX - rect.left) / rect.width) * 100, 0, 100),
        y: clamp(((clientY - rect.top) / rect.height) * 100, 0, 100),
      });
    },
    [updatePosition],
  );

  const frameClass = getProductFrameClass(product, orientation);

  return (
    <div className="space-y-6" data-testid="keepsake-image-position-editor">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#8b7355]">Position photo</p>
        <p className="mt-1 text-sm text-[#5c534a]">Drag the photo or use the controls to position it inside the frame.</p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div
          className={cn('relative mx-auto w-full max-w-[220px] touch-none select-none', frameClass)}
          onPointerDown={(e) => {
            dragRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            handlePointer(e.clientX, e.clientY, e.currentTarget);
          }}
          onPointerMove={(e) => {
            if (!dragRef.current) return;
            handlePointer(e.clientX, e.clientY, e.currentTarget);
          }}
          onPointerUp={() => {
            dragRef.current = false;
          }}
          onPointerCancel={() => {
            dragRef.current = false;
          }}
          role="img"
          aria-label="Drag to reposition photo inside frame"
        >
          <KeepsakeFrameMockup
            imageUrl={imageUrl}
            name={name}
            dates={dates}
            memorialTitle={memorialTitle}
            frameStyle={frameStyle}
            layout={{ ...layout, showNameDates: false, showTributeLine: false, showQrNote: false }}
            product={product}
            orientation={orientation}
            imagePosition={imagePosition}
            imageEdits={imageEdits}
            published={published}
            size="fill"
            showSafeArea
            className="w-full"
          />
        </div>

        <div className="min-w-0 flex-1 space-y-4">
          <div>
            <label className="text-xs font-medium text-[#5c534a]" htmlFor="keepsake-zoom">
              Zoom
            </label>
            <input
              id="keepsake-zoom"
              type="range"
              min="1"
              max="2.5"
              step="0.05"
              value={imagePosition.zoom ?? 1}
              onChange={(e) => updatePosition({ zoom: Number(e.target.value) })}
              className="mt-2 w-full accent-[hsl(152_32%_20%)]"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">{Math.round((imagePosition.zoom ?? 1) * 100)}%</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={onFit}>
              <Minimize2 className="mr-1 h-3.5 w-3.5" />
              Fit image
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={onFill}>
              <Maximize2 className="mr-1 h-3.5 w-3.5" />
              Fill frame
            </Button>
            <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={onReset}>
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Reset position
            </Button>
          </div>

          <p className="text-xs text-[#8b7355]">{MEMORIAL_PHOTO_LINK_SAFE_AREA}</p>
        </div>
      </div>

      <KeepsakeImageEnhanceControls
        imageEdits={imageEdits}
        onChange={onEditsChange}
        onReset={onResetEdits}
        onRotateLeft={onRotateLeft}
        onRotateRight={onRotateRight}
        disabled={!imageUrl}
      />
    </div>
  );
}

export default KeepsakeImagePositionEditor;
